
import { Component, Prop, Vue } from "vue-property-decorator";
import { PassengerChangeRequestStatus, UTILITIES } from "@/store/passenger-store";
import { INotice, INoticeUserState, NoticePriority } from '@/store/notice-store';

@Component
export default class PassengerListItem extends Vue {
  @Prop({ default: null }) passenger!: IPassenger;

  editPassenger(passenger: IPassenger) {
    this.$router.push({ name: "passenger-edit", params: { id: passenger.id } });
  }

  get nextTrip() {
    const nextTrip = this.$store.getters["trip/nextTripForPassenger"](this.passenger);
    if (!nextTrip) {
      return null;
    }
    return nextTrip.startDateTime || nextTrip.endDateTime;
  }

  get pendingChangeRequest(): IPassengerChangeRequest | undefined {
    if (!this.passenger.changeRequests) return undefined;
    return this.passenger.changeRequests.find(c => c.status === PassengerChangeRequestStatus.Pending);
  }

  get pendingUtilitiesDescription(): string {
    const parts: string[] = [
      ...this.pendingUnchangedNames,
      ...this.pendingAddedNames.map(n => `${n} ${this.$t("utility.added")}`),
      ...this.pendingRemovedNames.map(n => `${n} ${this.$t("utility.removed")}`),
    ];
    return parts.join(", ");
  }

  get pendingChangeSummary(): string {
    const parts: string[] = [];
    if (this.pendingRemovedNames.length > 0) {
      parts.push(this.pendingRemovedNames.join(", ") + " " + this.$t("utility.will_be_removed").toString().toLowerCase());
    }
    if (this.pendingAddedNames.length > 0) {
      parts.push(this.pendingAddedNames.join(", ") + " " + this.$t("utility.will_be_added").toString().toLowerCase());
    }
    if (parts.length === 0) return "";
    return this.$t("utility.pending_approval") as string + " " + parts.join(", ") + ".";
  }

  get currentUtilityNames(): string[] {
    if (!this.passenger.utilities) return [];
    return this.passenger.utilities
      .split(",")
      .map(s => Number.parseInt(s.trim(), 10))
      .filter(id => !Number.isNaN(id))
      .map(id => {
        const utility = UTILITIES.find(u => u.id === id);
        return utility ? this.$t("utility." + utility.key) as string : "";
      })
      .filter(name => name.length > 0);
  }

  get pendingUnchangedNames(): string[] {
    if (!this.passenger.utilities || !this.pendingChangeRequest) return [];
    const removedIds = this.pendingChangeRequest.removedUtilities
      ? this.pendingChangeRequest.removedUtilities.split(",").map(s => Number.parseInt(s.trim(), 10)).filter(id => !Number.isNaN(id))
      : [];
    return this.passenger.utilities
      .split(",")
      .map(s => Number.parseInt(s.trim(), 10))
      .filter(id => !Number.isNaN(id))
      .filter(id => !removedIds.includes(id))
      .map(id => {
        const utility = UTILITIES.find(u => u.id === id);
        return utility ? this.$t("utility." + utility.key) as string : "";
      })
      .filter(name => name.length > 0);
  }

  get pendingAddedNames(): string[] {
    if (!this.pendingChangeRequest?.addedUtilities) return [];
    return this.pendingChangeRequest.addedUtilities
      .split(",")
      .map(s => Number.parseInt(s.trim(), 10))
      .filter(id => !Number.isNaN(id))
      .map(id => {
        const utility = UTILITIES.find(u => u.id === id);
        return utility ? this.$t("utility." + utility.key) as string : "";
      })
      .filter(name => name.length > 0);
  }

  get pendingRemovedNames(): string[] {
    if (!this.pendingChangeRequest?.removedUtilities) return [];
    return this.pendingChangeRequest.removedUtilities
      .split(",")
      .map(s => Number.parseInt(s.trim(), 10))
      .filter(id => !Number.isNaN(id))
      .map(id => {
        const utility = UTILITIES.find(u => u.id === id);
        return utility ? this.$t("utility." + utility.key) as string : "";
      })
      .filter(name => name.length > 0);
  }

  get passengerNotices() {
    return this.$store.getters['notice/passengerNotices'](this.passenger?.movitId) as INotice[];
  }

  get showNoticesButton() {
    return this.passengerNotices.length > 0;
  }

  get unreadNoticesCount() {
    return this.passengerNotices.filter(n => {
      if (n.priority === NoticePriority.Sticky) {
        return false;
      }
      const noticeState = this.$store.getters['notice/noticeUserStateByNoticeId'](n.id) as INoticeUserState | null;
      return !noticeState || !noticeState.readAt;
    }).length;
  }

  openPassengerNotices() {
    const passengerId = this.passenger?.movitId;
    if (passengerId === undefined || passengerId === null) {
      return;
    }

    this.$router.push({ name: 'notices', query: { passengerId: String(passengerId) } });
  }
}
